import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect, useState } from 'react';
import { createCampaignSchema } from '../../assets/schemas/schemas';
import { initialPager } from '../../components/views/Parameters/parametersConstants';
import { notify } from '../../assets/helpers/toast';
import { useForm } from 'react-hook-form';
import { inactiveRowCount } from '../../components/views/Parameters/parametersTableUtils';
import { campaignBegin } from '../../services/campaignBeginService';
import { capitalize } from '@material-ui/core';

export default function useCampaign() {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    setValue,
    getValues,
    control,
  } = useForm({
    resolver: yupResolver(createCampaignSchema()),
    defaultValues: {
      note: '',
      campaignName: '',
      delay: 0,
      dateSince: '',
      dateUntil: '',
    },
  });

  const [showCampingForm, setShowCampingForm] = useState(false);

  const [campaignData, setCampingData] = useState({
    limit: initialPager.limit,
    page: 1,
    data: [],
    count: 0,
    isLoading: false,
  });

  const [editingCampaignId, setEditingCampaignId] = useState(null);

  const handleCampaignPageChange = useCallback((_e, page) => {
    setCampingData((prev) => ({
      ...prev,
      page,
    }));
  }, []);

  const handleToggleCampaignActive = useCallback(async (row) => {
    const next = capitalize(row.status);
    const response = await campaignBegin.changeStatusCampaign(row._id);

    if ([200, 201].includes(response?.status)) {
      notify(`El estatus de la campaña paso de ${next} a Eliminado`, 'success');
      setCampingData((prev) => ({
        ...prev,
        data: prev.data.map((item) =>
          item._id === row._id ? { ...item, status: 'eliminada' } : item
        ),
      }));
    } else {
      notify(
        response?.message ??
          response?.data?.message ??
          'Error al actualizar el estado de la campaña',
        'error'
      );
    }
  }, []);

  const handleEditCampaign = useCallback(
    (row) => {
      setShowCampingForm(true);
      setEditingCampaignId(row._id);
      reset({
        note: row.note ?? '',
        campaignName: row.campaignName ?? '',
        delay: row.delay ?? 0,
        dateSince: row.dateSince ? new Date(row.dateSince) : '',
        dateUntil: row.dateUntil ? new Date(row.dateUntil) : '',
        status: row.status,
      });
    },
    [reset]
  );

  const onCampaignSelectPerPage = (event) => {
    const value = Number(event.target.value);
    setCampingData((prev) => ({
      ...prev,
      page: 1,
      limit: value,
    }));
  };

  const fetchCampaign = useCallback(
    async (abortSignal) => {
      setCampingData((prev) => ({ ...prev, isLoading: true }));

      try {
        const response = await campaignBegin.getCampaign({
          page: campaignData.page,
          limit: campaignData.limit,
        });

        if (abortSignal?.aborted) return;

        if ([200, 201].includes(response?.status)) {
          const rows =
            response?.data?.data?.map((campaign) => ({
              ...campaign,
            })) || [];

          setCampingData((prev) => ({
            ...prev,
            data: rows,
            count: response?.data?.total ?? rows.length,
            isLoading: false,
          }));
        } else {
          notify(
            response?.message ??
              response?.data?.message ??
              'Error al cargar la lista de campañas',
            'error'
          );
          setCampingData((prev) => ({
            ...prev,
            isLoading: false,
          }));
        }
      } catch (error) {
        if (abortSignal?.aborted) return;
        notify('Error al cargar la lista de campañas', 'error');
        setCampingData((prev) => ({ ...prev, isLoading: false }));
      }
    },
    [campaignData.page, campaignData.limit]
  );

  const onSubmit = handleSubmit(async (data) => {
    delete data.status
    if (editingCampaignId) {
      const response = await campaignBegin.updateCampaign(
        editingCampaignId,
        {
          ...data,
          dateSince: data.dateSince ? new Date(data.dateSince).toISOString() : '',
          dateUntil: data.dateUntil ? new Date(data.dateUntil).toISOString() : '',
        } // Asegúrate de enviar el ID del canal de venta y las fechas en formato ISO
      );

      if ([200, 201].includes(response?.status)) {
        notify('Campaña actualizada', 'success');
        fetchCampaign();
        setEditingCampaignId(null);
        setShowCampingForm(false);
        reset({
          note: '',
          campaignName: '',
          delay: 0,
          dateSince: '',
          dateUntil: '',
          status: '',
        });
      } else {
        notify(
          response?.message ??
            response?.data?.message ??
            'Error al actualizar la campaña',
          'error'
        );
      }
      return;
    }

    const response = await campaignBegin.createCampaign({
      ...data,
      dateSince: data.dateSince ? new Date(data.dateSince).toISOString() : '',
      dateUntil: data.dateUntil ? new Date(data.dateUntil).toISOString() : '',
    });

    if ([200, 201].includes(response?.status)) {
      notify('Campaña creada', 'success');
      reset({
        note: '',
        campaignName: '',
        delay: 0,
        dateSince: '',
        dateUntil: '',
        status: '',
      });
      setShowCampingForm(false);
      fetchCampaign();
    } else {
      notify(
        response?.message ??
          response?.data?.message ??
          'Error al crear la campaña',
        'error'
      );
    }
  });

  const closeForm = () => {
    setShowCampingForm(false);
    setEditingCampaignId(null);
    reset({
      note: '',
      campaignName: '',
      delay: 0,
      dateSince: '',
      dateUntil: '',
      status: '',
    });
  };

  useEffect(() => {
    const abortController = new AbortController();
    fetchCampaign(abortController.signal);

    return () => {
      abortController.abort();
    };
  }, [fetchCampaign]);

  const inactiveCampaigns = inactiveRowCount(campaignData.data);

  return {
    register,
    errors,
    showCampingForm,
    setShowCampingForm,
    campaignData,
    editingCampaignId,
    handleCampaignPageChange,
    handleToggleCampaignActive,
    handleEditCampaign,
    onSubmit,
    onCampaignSelectPerPage,
    inactiveCampaigns,
    closeForm,
    setValue,
    getValues,
    control,
  };
}
